import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';

const SELF_REPORT_LABELS = {
  EASY: '++ — получилось легко',
  OK_NO_PEEK: '+ — самостоятельно',
  OK_WITH_PEEK: '+- — с подсказкой / после сверки',
  OK_WITH_QUESTION: '+? — получилось, но есть вопрос',
  FAILED: '− — не получилось',
  NO_IDEAS: '−− — нет идей',
  NOT_TRIED: '! — даже не пробовал',
};

const VERDICT_LABELS = {
  ACCEPTED: 'Принято',
  REVIEW_IN_LESSON: 'Посмотрим на занятии',
  REDO: 'Переделать',
};

function groupAttemptsByDay(attemptsList) {
  const groups = new Map();
  for (const a of attemptsList || []) {
    const key = a?.created_at ? new Date(a.created_at).toLocaleDateString('ru-RU') : '—';
    const arr = groups.get(key) || [];
    arr.push(a);
    groups.set(key, arr);
  }
  return Array.from(groups.entries());
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

export default function ParentStudentActivityPage() {
  const { studentId } = useParams();
  const [childName, setChildName] = useState('');
  const [days, setDays] = useState(30);
  const [problemOnly, setProblemOnly] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/parent/children/');
        const list = Array.isArray(res.data) ? res.data : [];
        const child = list.find((c) => Number(c.id) === Number(studentId));
        if (!cancelled) {
          setChildName(child ? child.display_name : '');
        }
      } catch {
        if (!cancelled) setChildName('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  const loadAttempts = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    setError('');
    try {
      const params = {
        days: days === 0 ? undefined : days,
        problem: problemOnly ? 1 : undefined,
      };
      const res = await api.get(`/parent/children/${studentId}/attempts/`, { params });
      setAttempts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Не удалось загрузить журнал.';
      setError(typeof msg === 'string' ? msg : 'Не удалось загрузить журнал.');
      setAttempts([]);
    } finally {
      setLoading(false);
    }
  }, [studentId, days, problemOnly]);

  useEffect(() => {
    void loadAttempts();
  }, [loadAttempts]);

  return (
    <div className="parent-home-page">
      <div className="page-header">
        <p className="page-subtitle">
          <Link to="/parent" className="back-link">← Кабинет родителя</Link>
        </p>
        <h1>Журнал попыток</h1>
        <p className="page-subtitle">
          {childName ? `${childName}: ` : ''}
          что решали, самооценка ученика и комментарий преподавателя. Код решений не показываем.
          {' · '}
          <Link to={`/parent/children/${studentId}/topic-progress`}>План занятий</Link>
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="parent-journal-filters">
        <label className="text-secondary">
          Период
          <select className="form-select" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={7}>7 дней</option>
            <option value={30}>30 дней</option>
            <option value={90}>90 дней</option>
            <option value={0}>всё время</option>
          </select>
        </label>
        <label className="parent-journal-check">
          <input
            type="checkbox"
            checked={problemOnly}
            onChange={(e) => setProblemOnly(e.target.checked)}
          />
          только проблемные
        </label>
      </div>

      {loading ? (
        <p className="text-secondary">Загрузка журнала…</p>
      ) : attempts.length === 0 ? (
        <p className="text-secondary">Пока нет событий.</p>
      ) : (
        <div className="parent-attempt-groups">
          {groupAttemptsByDay(attempts).map(([day, items]) => (
            <section key={day} className="parent-attempt-day">
              <h2>{day}</h2>
              <ul className="parent-attempt-list">
                {items.map((a) => (
                  <li key={a.id} className="parent-attempt-card">
                    <div className="parent-attempt-head">
                      <strong>{a.task_title || a.task_slug || 'Задача'}</strong>
                      <span className="text-secondary">{fmtDateTime(a.created_at)}</span>
                    </div>
                    {a.task_list_title ? (
                      <div className="text-secondary">Список: {a.task_list_title}</div>
                    ) : null}
                    <div className="parent-attempt-meta">
                      Ученик: {SELF_REPORT_LABELS[a.self_report_status] || a.self_report_status || 'без самооценки'}
                      {a.teacher_verdict ? (
                        <>
                          {' · '}
                          преподаватель: {VERDICT_LABELS[a.teacher_verdict] || a.teacher_verdict}
                        </>
                      ) : null}
                    </div>
                    {a.self_comment ? (
                      <div className="parent-attempt-comment">
                        <strong>Отчёт ученика</strong>
                        <pre className="progress-note-readonly">{a.self_comment}</pre>
                      </div>
                    ) : null}
                    {a.teacher_comment ? (
                      <div className="parent-attempt-comment">
                        <strong>Комментарий преподавателя</strong>
                        <pre className="progress-note-readonly">{a.teacher_comment}</pre>
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
